// CatTrax admins: what permission checks refused, and what they would refuse or allow - the log-only
// checks once enforced, the saved changes, and the proposed defaults - so anyone who would lose access can
// be sorted out before any of them is enforced. Each row is one permission at one practice with its outcome
// today, with the saved changes, and under the proposal; open it to see who was counted, why, and on which
// endpoints. Shown in a full-screen dialog (SettingsDialog.openPermissionAuditLog), laid out like Settings.
app.component("permissionRefusalsAdminConsole", {
	templateUrl: "app/src/js/components/admin/permissionRefusalsConsole.html",
	controller: "permissionRefusalsAdminController",
});

app.controller("permissionRefusalsAdminController", [
	"$mdDialog",
	"$state",
	"$window",
	"PermissionAPI",
	"Session",
	"SettingsDialog",
	"USER_ROLE",
	"toast",
	function ($mdDialog, $state, $window, PermissionAPI, Session, SettingsDialog, USER_ROLE, toast) {
		var $ctrl = this;
		$ctrl.dayOptions = [7, 14, 31];
		$ctrl.days = 14;
		$ctrl.rows = [];
		// CatTrax staff and test practices skew the counts, so they are left out unless asked for.
		$ctrl.include = { admins: false, testPractices: false };
		// An object, so the inputs inside ng-if blocks write to this controller rather than their own scope.
		$ctrl.filter = { text: "", practiceId: null, role: null };
		$ctrl.expanded = {};

		// The kinds the API counts.
		$ctrl.kindOptions = [
			{
				id: "REFUSED",
				label: "Refused",
				icon: "fa-ban",
				help: "Requests that were refused with an access-denied message, under the defaults in force. "
					+ "\"Under proposal\" says whether the proposed defaults would have let them through.",
			},
			{
				id: "LOG_ONLY",
				label: "Would be refused once enforced",
				icon: "fa-eye",
				help: "The checks on endpoints that had none before only log what they would refuse for now. Before "
					+ "switching enforcement on, give the access that's really needed: change the role, or add an "
					+ "exception at the practice.",
			},
			{
				id: "CHANGES_REFUSE",
				label: "Saved changes would refuse",
				icon: "fa-toggle-off",
				help: "Requests let through today that the changes saved on the permission screens (CatTrax's role "
					+ "defaults, a practice switching a permission off, a personal exception, a practice's change to "
					+ "someone's roles) would refuse. Those changes are only logged for now, so nobody has lost access.",
			},
			{
				id: "CHANGES_ALLOW",
				label: "Saved changes would allow",
				icon: "fa-toggle-on",
				help: "Requests refused today, or that would be once enforced, that the saved changes would let "
					+ "through. The changes are only logged for now.",
			},
			{
				id: "PROPOSAL_REFUSES",
				label: "Proposed defaults would refuse",
				icon: "fa-minus-circle",
				help: "Requests the saved changes would let through that the proposed role defaults (the Roles and "
					+ "Permissions sheet) would refuse. These people would lose access if the proposal were put in force.",
			},
			{
				id: "PROPOSAL_ALLOWS",
				label: "Proposed defaults would allow",
				icon: "fa-plus-circle",
				help: "Requests the saved changes would refuse that the proposed role defaults would let through.",
			},
		];
		$ctrl.kind = null;

		$ctrl.$onInit = function () {
			$ctrl.load($ctrl.days);
		};

		$ctrl.load = function (days) {
			$ctrl.days = days;
			$ctrl.isProcessing = true;
			$ctrl.loadError = null;
			$ctrl.expanded = {};
			PermissionAPI.getRefusals(days, {
				includeAdmins: $ctrl.include.admins,
				includeTestPractices: $ctrl.include.testPractices,
			})
				.then(function (response) {
					$ctrl.rows = response.data;
					$ctrl.practiceOptions = practiceOptions($ctrl.rows);
					$ctrl.roleOptions = roleOptions($ctrl.rows);
				})
				.catch(function (response) {
					$ctrl.rows = [];
					$ctrl.loadError = PermissionAPI.errorMessage(response, "The permissions audit log could not be loaded.");
				})
				.finally(function () {
					$ctrl.isProcessing = false;
				});
		};

		$ctrl.toggleInclude = function (what) {
			$ctrl.include[what] = !$ctrl.include[what];
			$ctrl.load($ctrl.days);
		};

		$ctrl.selectKind = function (kind) {
			$ctrl.kind = kind;
		};

		$ctrl.kindOption = function (kindId) {
			return $ctrl.kindOptions.find(function (option) {
				return option.id === kindId;
			});
		};

		$ctrl.clearFilters = function () {
			$ctrl.filter.text = "";
			$ctrl.filter.practiceId = null;
			$ctrl.filter.role = null;
		};

		$ctrl.hasFilters = function () {
			return !!($ctrl.filter.text || $ctrl.filter.practiceId || $ctrl.filter.role);
		};

		// The rows of one kind (every kind when none is given) that match the filters.
		function rowsOfKind(kindId) {
			return $ctrl.rows.filter(function (row) {
				return (!kindId || row.kind === kindId) && matchesFilters(row);
			});
		}

		function matchesFilters(row) {
			if ($ctrl.filter.practiceId && row.practice_id !== $ctrl.filter.practiceId) {
				return false;
			}
			if ($ctrl.filter.role && !row.roles.some(function (count) {
				return count.role === $ctrl.filter.role;
			})) {
				return false;
			}
			var text = ($ctrl.filter.text || "").trim().toLowerCase();
			if (!text) {
				return true;
			}
			var haystack = [row.permission, row.permission_label, row.practice_name].concat(
				row.endpoints.map(function (endpoint) {
					return endpoint.endpoint;
				}),
				row.users.map(function (user) {
					return (user.name || "") + " " + (user.username || "");
				}),
			);
			return haystack.some(function (value) {
				return value && value.toLowerCase().indexOf(text) !== -1;
			});
		}

		$ctrl.shownRows = function () {
			return rowsOfKind($ctrl.kind);
		};

		$ctrl.roleLabel = function (roleId) {
			if (roleId === "ADMIN") {
				return "CatTrax admin";
			}
			var role = USER_ROLE.find(function (option) {
				return option.id === roleId;
			});
			return role ? role.label : roleId;
		};

		$ctrl.rolesLabel = function (roles) {
			return roles.length ? roles.map(function (role) {
				return $ctrl.roleLabel(role);
			}).join(", ") : "No role";
		};

		// The requests counted, for one kind, or for whatever is shown when no kind is given.
		$ctrl.totalRefusals = function (kindId) {
			return rowsOfKind(kindId || $ctrl.kind).reduce(function (total, row) {
				return total + row.refusals;
			}, 0);
		};

		// Rows are told apart by everything the API groups them by.
		$ctrl.rowKey = function (row) {
			return [row.kind, row.today_outcome, row.changes_outcome, row.proposal_outcome, row.permission,
				row.practice_id].join("|");
		};

		$ctrl.toggleRow = function (row) {
			var key = $ctrl.rowKey(row);
			$ctrl.expanded[key] = !$ctrl.expanded[key];
		};

		$ctrl.isExpanded = function (row) {
			return !!$ctrl.expanded[$ctrl.rowKey(row)];
		};

		$ctrl.personLabel = function (user) {
			return user.name || user.username || (user.user_id ? "User " + user.user_id : "Unknown user");
		};

		// Worked out again only when the rows or what is shown change: the template repeats over its lists,
		// and new lists on every digest would never settle.
		var impactCache = { rows: null, key: null, value: null };

		// What the screen shows, summed up: who would lose or gain access, and where. The same person is
		// counted once however many rows they are in.
		$ctrl.impact = function () {
			var key = [$ctrl.kind, $ctrl.filter.text, $ctrl.filter.practiceId, $ctrl.filter.role].join("|");
			if (impactCache.rows !== $ctrl.rows || impactCache.key !== key) {
				impactCache = { rows: $ctrl.rows, key: key, value: summarise(rowsOfKind($ctrl.kind)) };
			}
			return impactCache.value;
		};

		function summarise(rows) {
			var losing = rows.filter(function (row) {
				return row.today_outcome !== "REFUSED" && row.proposal_outcome === "REFUSED";
			});
			var gaining = rows.filter(function (row) {
				return row.today_outcome !== "ALLOWED" && row.proposal_outcome === "ALLOWED";
			});
			var refused = rows.filter(function (row) {
				return row.today_outcome === "REFUSED";
			});
			var changesRefuse = rows.filter(function (row) {
				return row.today_outcome !== "REFUSED" && row.changes_outcome === "REFUSED";
			});
			var changesAllow = rows.filter(function (row) {
				return row.today_outcome !== "ALLOWED" && row.changes_outcome === "ALLOWED";
			});
			var logOnly = rows.filter(function (row) {
				return row.kind === "LOG_ONLY";
			});
			var logOnlyEndpoints = {};
			logOnly.forEach(function (row) {
				row.endpoints.forEach(function (endpoint) {
					logOnlyEndpoints[endpoint.endpoint] = (logOnlyEndpoints[endpoint.endpoint] || 0) + endpoint.refusals;
				});
			});
			return {
				losing: peopleAndPractices(losing),
				gaining: peopleAndPractices(gaining),
				refused: peopleAndPractices(refused),
				changesRefuse: peopleAndPractices(changesRefuse),
				changesAllow: peopleAndPractices(changesAllow),
				topPermissions: top(rows, function (row) {
					return row.permission;
				}, function (row) {
					return row.permission_label || row.permission;
				}),
				topPractices: top(rows, function (row) {
					return row.practice_id;
				}, function (row) {
					return row.practice_name || (row.practice_id ? "Practice " + row.practice_id : "No practice");
				}),
				logOnlyEndpoints: Object.keys(logOnlyEndpoints).map(function (endpoint) {
					return { endpoint: endpoint, refusals: logOnlyEndpoints[endpoint] };
				}).sort(function (a, b) {
					return b.refusals - a.refusals;
				}),
			};
		};

		function personKey(user) {
			return user.user_id || user.username || "unknown";
		}

		function peopleAndPractices(rows) {
			var people = {};
			var practices = {};
			rows.forEach(function (row) {
				practices[row.practice_id] = true;
				row.users.forEach(function (user) {
					people[personKey(user)] = true;
				});
			});
			return { people: Object.keys(people).length, practices: Object.keys(practices).length };
		}

		// The five groups with the most different people in them.
		function top(rows, keyOf, labelOf) {
			var groups = {};
			rows.forEach(function (row) {
				var key = keyOf(row);
				var group = groups[key] || (groups[key] = { label: labelOf(row), people: {}, refusals: 0 });
				group.refusals += row.refusals;
				row.users.forEach(function (user) {
					group.people[personKey(user)] = true;
				});
			});
			return Object.keys(groups).map(function (key) {
				var group = groups[key];
				return { label: group.label, people: Object.keys(group.people).length, refusals: group.refusals };
			}).sort(function (a, b) {
				return b.people - a.people || b.refusals - a.refusals;
			}).slice(0, 5);
		}

		function practiceOptions(rows) {
			var byId = {};
			rows.forEach(function (row) {
				if (row.practice_id) {
					byId[row.practice_id] = row.practice_name || "Practice " + row.practice_id;
				}
			});
			return Object.keys(byId).map(function (id) {
				return { id: Number(id), name: byId[id] };
			}).sort(function (a, b) {
				return a.name.localeCompare(b.name);
			});
		}

		function roleOptions(rows) {
			var roles = {};
			rows.forEach(function (row) {
				row.roles.forEach(function (count) {
					roles[count.role] = true;
				});
			});
			return Object.keys(roles).map(function (role) {
				return { id: role, label: $ctrl.roleLabel(role) };
			}).sort(function (a, b) {
				return a.label.localeCompare(b.label);
			});
		}

		// Jump to fix it.

		// What every practice's role is given, on the CatTrax role-defaults screen, which is a page of its own.
		$ctrl.openRoleDefaults = function (row, role) {
			$mdDialog.hide();
			$state.go("admin-console", { type: "permissions", role: role || null, permission: row.permission });
		};

		// The practice's own settings, opened over this dialog so closing them comes back here. They are only
		// shown for the practice being worked in, so switch to it first, after asking.
		$ctrl.openPracticeRoles = function (row, role) {
			switchToPractice(row, function () {
				SettingsDialog.openSettings("rolePermissions", { focus: { role: role || null }, multiple: true });
			});
		};

		$ctrl.openPersonException = function (row, user) {
			switchToPractice(row, function () {
				SettingsDialog.openSettings("staff", { focus: { userId: user.user_id }, multiple: true });
			});
		};

		$ctrl.canOpenPractice = function (row) {
			return !!findPractice(row.practice_id);
		};

		function findPractice(practiceId) {
			return (Session.practiceOptions || []).find(function (practice) {
				return practice.id === practiceId;
			});
		}

		function switchToPractice(row, then) {
			var practice = findPractice(row.practice_id);
			if (!practice) {
				toast.create({
					timeout: 8 * 1000,
					message: "You can't switch to " + (row.practice_name || "this practice") + ", so its settings can't be opened.",
					className: "alert-danger",
					dismissible: true,
				});
				return;
			}
			if (!Session.practice || Session.practice.id !== practice.id) {
				if (!$window.confirm("This switches you to " + practice.name + " to open its settings. Continue?")) {
					return;
				}
				Session.setPractice(practice);
			}
			then();
		}

		// CSV of what is shown, one line per person per row, for the Roles and Permissions sheet's owners.

		var CSV_COLUMNS = [
			"Kind", "Today", "With saved changes", "Under proposal", "Permission", "Permission label", "Practice id",
			"Practice", "User id", "Name", "Username", "Roles at practice", "CatTrax staff", "Requests", "First seen",
			"Last seen", "Why today", "Why with saved changes", "Why under proposal", "Endpoints",
		];

		$ctrl.exportCsv = function () {
			var lines = [CSV_COLUMNS];
			$ctrl.shownRows().forEach(function (row) {
				row.users.forEach(function (user) {
					lines.push([
						row.kind_label, row.today_outcome_label, row.changes_outcome_label, row.proposal_outcome_label,
						row.permission,
						row.permission_label, row.practice_id, row.practice_name, user.user_id, user.name, user.username,
						$ctrl.rolesLabel(user.roles), user.cattrax_admin ? "Yes" : "No", user.refusals,
						user.first_seen, user.last_seen, user.today_reason_label, user.changes_reason_label,
						user.proposal_reason_label,
						user.endpoints.map(function (endpoint) {
							return endpoint.endpoint + " (" + endpoint.refusals + ")";
						}).join("; "),
					]);
				});
			});
			var csv = lines.map(function (line) {
				return line.map(csvCell).join(",");
			}).join("\r\n");
			download(csv, "permissions-audit-log-" + new Date().toISOString().slice(0, 10) + ".csv");
		};

		// Quoted, and a leading = + - or @ made inert, so a spreadsheet never runs a cell as a formula.
		function csvCell(value) {
			var text = value === null || value === undefined ? "" : String(value);
			if (/^[=+\-@]/.test(text)) {
				text = "'" + text;
			}
			return "\"" + text.replace(/"/g, "\"\"") + "\"";
		}

		function download(csv, filename) {
			// The byte order mark makes Excel read the file as UTF-8, so names with accents survive.
			var blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
			var link = document.createElement("a");
			link.href = URL.createObjectURL(blob);
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(link.href);
		}
	},
]);
